import { filterCurrencyPairs } from '@/components/sg-design-system/components/input/SgCurrencyPairPicker/filterCurrencyPairs.ts';
import type { ActionWithPayload } from '@/components/sg-design-system/utils/reducerify-types.ts';
import { forState } from '@/components/sg-design-system/utils/reducerify.ts';

import type { SgCurrencyPairPickerState } from './state.ts';

export const { reducer, actions } = forState<SgCurrencyPairPickerState>().createReducer({
  valueChanged: (state, action: ActionWithPayload<{ value: string }>) => {
    const hasError = !state.allCurrencyPairs.includes(action.payload.value);

    return {
      ...state,
      value: action.payload.value,
      editedValue: action.payload.value,

      currencyPairSelected: !hasError ? action.payload.value : '',
      hasError,
    };
  },

  currencyPairsChanged: (state, action: ActionWithPayload<{ currencyPairs: string[] }>) => {
    const { currencyPairs } = action.payload;

    return {
      ...state,
      allCurrencyPairs: currencyPairs,
      filteredCurrencyPairs: slicedFilterCurrencyPairs(currencyPairs, state.editedValue), // Reset filtered pairs to all pairs
      // selectedCurrencyPair: state.selectedCurrencyPair || currencyPairs[0] || '',
      //editedValue: state.editedValue || currencyPairs[0] || '',
      hasError: false, // Reset error state when currency pairs change
    };
  },

  inputFocused: (state) => {
    return {
      ...state,
      hasError: false,
    };
  },

  inputBlurred: (state) => {
    const hasError =
      !state.allCurrencyPairs.includes(state.editedValue) || (state.editedValue.length === 0 && state.isRequired);

    return {
      ...state,
      editedValue: hasError ? state.value : state.editedValue,
      hasError,
      isTooltipOpen: false,
    };
  },

  valueEdited: (state, action: ActionWithPayload<{ value: string }>) => {
    const editedValue = action.payload.value.toUpperCase();

    const filteredCurrencyPairs = slicedFilterCurrencyPairs(state.allCurrencyPairs, editedValue);

    return {
      ...state,
      editedValue,
      filteredCurrencyPairs,
      hasError: false, // Reset error state on edit
    };
  },

  valueReset: (state) => {
    return {
      ...state,
      editedValue: state.value,
      selectedCurrencyPair: state.value,
      hasError: false,
      isTooltipOpen: false,
    };
  },

  currencyPairSelected: (state, action: ActionWithPayload<{ currencyPair: string }>) => {
    const { currencyPair } = action.payload;

    const hasError = !state.allCurrencyPairs.includes(currencyPair);

    return {
      ...state,
      selectedCurrencyPair: !hasError ? currencyPair : state.filteredCurrencyPairs[0] || '',
      editedValue: hasError ? state.value : currencyPair,
      hasError,
    };
  },

  currencyPairsFiltered: (state, action: ActionWithPayload<{ debouncedCurrentValue: string }>) => {
    const { debouncedCurrentValue } = action.payload;

    if (debouncedCurrentValue.length === 0) {
      return state;
    }

    return {
      ...state,
      filteredCurrencyPairs: slicedFilterCurrencyPairs(state.allCurrencyPairs, debouncedCurrentValue),
    };
  },

  errorTooltipOpeningChanged: (state, action: ActionWithPayload<{ isOpen: boolean }>) => {
    return {
      ...state,
      isTooltipOpen: action.payload.isOpen,
    };
  },
});

const slicedFilterCurrencyPairs = (currencyPairs: string[], searchTerm: string) => {
  // perf consideration: limit to first 7 results
  // because currently all currency pair item of each currency pair picker is in the DOM, visible or not
  const filtered = filterCurrencyPairs(currencyPairs, searchTerm);
  // Exact matches go first so that pressing Enter selects the typed pair (e.g. 'USD/EUR' over 'EUR/USD')
  const exact = filtered.filter((p) => p === searchTerm);
  const rest = filtered.filter((p) => p !== searchTerm);

  return [...exact, ...rest].slice(0, 7);
};
